/**
 * App mounts providers and the generated router. A local-only sign-out is a
 * special fail-closed state: while remote refresh-session revocation is
 * uncertain, automatic cookie-based restoration is suppressed. A subsequent
 * explicit successful login updates auth state, clears the marker, and mounts
 * the normal SessionProvider again.
 */
import { useEffect, useMemo, useState } from 'react';
import { useAtomValue } from 'jotai';
import { RouterProvider } from 'react-router-dom';
import { ErrorBoundary } from 'react-error-boundary';
import { PageErrorState } from './components/states';
import {
  SessionContext,
  SessionProvider,
  type SessionContextValue,
} from './features/auth/session-provider';
import { authAtom } from './features/auth/auth.atoms';
import {
  isLocalSignOutSuppressed,
  setLocalSignOutSuppressed,
} from './features/auth/local-signout';
import { ExperienceProvider } from './features/experience/ExperienceProvider';
import { createRouter } from './runtime/routes/create-router';

function ErrorFallback() {
  return <PageErrorState reasonCode="INTERNAL_ERROR" />;
}

const LOCAL_SIGNED_OUT_SESSION: SessionContextValue = {
  retry: () => {},
  retryCount: 0,
  user: null,
  isAuthenticated: false,
  loading: false,
  restorationState: 'anonymous',
  restorationReason: null,
  logout: async () => {},
  refreshProfile: async () => null,
  currentContext: null,
  availableContexts: [],
  switchContext: async () => {},
  exitDelegation: async () => {},
  contextSwitchState: 'idle',
  contextSwitchError: null,
  invalidateRestoration: () => {},
};

function AuthSessionBoundary({ children }: { children: React.ReactNode }) {
  const auth = useAtomValue(authAtom);
  const [suppressed, setSuppressed] = useState(() => isLocalSignOutSuppressed());

  useEffect(() => {
    if (!auth.isAuthenticated || !suppressed) return;
    setLocalSignOutSuppressed(false);
    setSuppressed(false);
  }, [auth.isAuthenticated, suppressed]);

  if (suppressed) {
    return (
      <SessionContext.Provider value={LOCAL_SIGNED_OUT_SESSION}>
        {children}
      </SessionContext.Provider>
    );
  }
  return <SessionProvider>{children}</SessionProvider>;
}

export function App() {
  const router = useMemo(() => createRouter(), []);

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <AuthSessionBoundary>
        <ExperienceProvider>
          <RouterProvider router={router} />
        </ExperienceProvider>
      </AuthSessionBoundary>
    </ErrorBoundary>
  );
}
